const STATUS_NOK = 'NOK';
const STATUS_OK = 'OK';
const STATUS_GREAT = 'GREAT';
const STATUS_MEDIUM = 'MEDIUM';
const STATUS_NON_MON = 'NON_MON';
const STATUS_NONE = 'NONE';

const COLOR_EXPECTED = '#585858';
const COLOR_ACTUAL = '#19BD60';
const COLOR_RATIO = '#e2ce4f';

const PRIMARY = '#FFAB05';
const PRIMARY_800 = '#FFBC57';
const PRIMARY_600 = '#FFCD8C';

// biome-ignore lint/complexity/noStaticOnlyClass: necessary evil
export default class Colors {
  public static readonly COLOR_INFO = '#8BCDD4';
  public static readonly COLOR_OK = '#19B555';
  public static readonly COLOR_GREAT = '#800080';
  public static readonly COLOR_NOK = '#F64161';
  public static readonly COLOR_MEDIUM = '#FFBC57';
  public static readonly COLOR_NONE = '#72898D';
  public static readonly COLOR_NON_MON = '#17a2b8';

  // Based on status index
  static colorByStatus(status: string): string {
    switch (status) {
      case STATUS_NOK:
        return Colors.COLOR_NOK; //Red
      case STATUS_OK:
        return Colors.COLOR_OK; //Green
      case STATUS_MEDIUM:
        return Colors.COLOR_MEDIUM; //Orange
      case STATUS_NON_MON:
        return Colors.COLOR_NON_MON;
      case STATUS_GREAT:
        return Colors.COLOR_GREAT;
      default:
        return Colors.COLOR_NONE; //Grey
    }
  }

  static nonMonitoredColor(): string {
    return Colors.COLOR_NON_MON;
  }

  static colorBySeverity(severity: number): string {
    return Colors.colorByStatus(Colors.statusBySeverity(severity));
  }

  static statusBySeverity(severity: number): string {
    if (severity >= 100) {
      return STATUS_NOK;
    } else if (severity >= 75) {
      return STATUS_MEDIUM;
    } else if (severity === 0) {
      return STATUS_OK;
    } else {
      return STATUS_NONE;
    }
  }

  static mapToStatusGradient(score: number): string {
    return score === undefined || score === null
      ? Colors.COLOR_NONE
      : Colors.mapToMultiGradient(
          score,
          Colors.COLOR_NOK,
          Colors.COLOR_MEDIUM,
          Colors.COLOR_OK,
        );
  }

  static colorByMeterStatusType(msType: any): string {
    // noinspection JSUnresolvedVariable
    return msType.relevant_for_status
      ? Colors.colorBySeverity(msType.severity)
      : Colors.COLOR_INFO;
  }

  // Yield Graph

  static expectedYieldColor(): string {
    return COLOR_EXPECTED;
  }

  static actualYieldColor(): string {
    return COLOR_ACTUAL;
  }

  static ratioColor(): string {
    return COLOR_RATIO;
  }

  // Primary gradient

  static primary(): string {
    return PRIMARY;
  }

  static primary800(): string {
    return PRIMARY_800;
  }

  static primary600(): string {
    return PRIMARY_600;
  }

  static unknownColor(): string {
    return Colors.COLOR_NONE;
  }

  // Universal

  static mapToMultiGradient(
    score: number,
    colorHex: string,
    ...colorHexes: string[]
  ): string {
    if (score <= 0 || !score) return colorHex;
    colorHexes.unshift(colorHex);
    if (score >= 1) return colorHexes[colorHexes.length - 1];

    const mappedScore: number = score * (colorHexes.length - 1);
    const index: number = Math.floor(mappedScore);

    return Colors.mapToGradient(
      mappedScore - index,
      colorHexes[index],
      colorHexes[index + 1],
    );
  }

  static mapToGradient(
    score: number,
    colorLowHex: string,
    colorHighHex: string,
  ): string {
    const colorLow = Colors.hexToRgb(colorLowHex);
    const colorHigh = Colors.hexToRgb(colorHighHex);

    return Colors.rgbToHex(
      Math.round((colorHigh.r - colorLow.r) * score + colorLow.r),
      Math.round((colorHigh.g - colorLow.g) * score + colorLow.g),
      Math.round((colorHigh.b - colorLow.b) * score + colorLow.b),
    );
  }

  static hexToRgb(hex: string): { r: number; g: number; b: number } {
    const result: RegExpExecArray =
      /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 255, g: 255, b: 255 };
  }

  static rgbToHex(r: number, g: number, b: number): string {
    const componentToHex = (c: number): string => {
      const hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
  }
}
